import React, { useContext, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TbPencil } from 'react-icons/tb';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { DeviceContext } from '@vyce/core/src/contexts';
import { sendEmailVerificationRequest } from '@vyce/core/src/api/auth';
import { changeEmailRequest } from '@vyce/core/src/api/users';
import { changeEmailByAdminRequest } from '@vyce/core/src/api/legend/users';
import { EmailField } from '@vyce/core/src/components/controlled-inputs';

import { NotificationContext } from '../../../contexts/notificationContext';
import { AppIconButton } from '../../../components/AppIconButton';
import { ButtonTitleWithLoading } from '../../../components/ButtonTitleWithLoading';

interface Props {
  email?: string;
  userId: string;
  isLegend?: boolean;
  verified: boolean;
  getReCaptchaToken: () => Promise<string>;
  /** Called after the email is successfully changed, with the new value. */
  onSaved?: (email: string) => void;
}

interface Form {
  email: string;
}

export const EditEmailOrVerify: React.FC<Props> = ({
  email: outerEmail,
  verified: outerVerified,
  userId,
  isLegend,
  getReCaptchaToken,
  onSaved,
}) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(outerEmail ?? '');
  const [verified, setVerified] = useState<boolean>(outerVerified ?? false);
  const [loading, setLoading] = useState<boolean>(false);

  const methods = useForm<Form>({
    defaultValues: {
      email: email,
    },
  });

  const sendEmailVerification = async () => {
    try {
      const recaptcha_token = await getReCaptchaToken();
      await sendEmailVerificationRequest({ userId, recaptcha_token });
      showNotification({ message: 'Verification link has been sent', options: { variant: 'success' } });
      setOpen(false);
    } catch (e) {
      handleServerError(e);
    }
  };

  const onSubmit = async (data: Form, event?: React.BaseSyntheticEvent<object, any, any>) => {
    event?.preventDefault();
    try {
      setLoading(true);
      const recaptcha_token = await getReCaptchaToken();
      if (isLegend) {
        await changeEmailByAdminRequest({ email: data.email, user_id: userId, recaptcha_token });
      } else {
        await changeEmailRequest({ email: data.email, recaptcha_token });
      }
      setEmail(data.email);
      setVerified(false);
      setOpen(false);
      onSaved?.(data.email);
      return false;
    } catch (e) {
      if ((e as any).status === 409) {
        return showNotification({
          message:
            'This email address is already registered to a Vyce account. Please use another email for this profile or login to your existing account. ',
          options: { variant: 'error' },
        });
      }
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box display="flex" height="100%" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex">
            <Typography variant="subtitle2" color="textSecondary">
              Email{' '}
            </Typography>
            <Typography
              style={{
                color: !verified ? theme.palette.error.main : theme.palette.text.secondary,
                marginLeft: 4,
              }}
              variant="subtitle2"
            >
              ({verified ? 'verified' : 'unverified'})
            </Typography>
            <Typography variant="subtitle2">:</Typography>
          </Box>
          <Typography variant="caption" style={{ fontWeight: 500 }}>
            {email}
          </Typography>
        </Box>

        <Box display="flex" height="100%" gridGap={8}>
          {!verified && !isLegend && (
            <Button variant="outlined" size="small" onClick={sendEmailVerification} color="primary">
              Verify
            </Button>
          )}

          <AppIconButton variant="primary" onClick={() => setOpen(true)}>
            <TbPencil color={theme.palette.primary.dark} size="18px" />
          </AppIconButton>
        </Box>
      </Box>

      <Dialog
        container={document.getElementById(MAIN_CONTAINER_ID)}
        fullScreen={isMobile}
        open={open}
        onClose={() => setOpen(false)}
      >
        <FormProvider {...methods}>
          <form>
            <DialogTitle>Change your email address</DialogTitle>
            <DialogContent>
              Please input your new email address and we’ll send you a verification link.
              <EmailField autoFocus margin="normal" />
            </DialogContent>
            <DialogActions>
              <Button size="large" variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="large"
                type="button"
                style={{ width: 100 }}
                disabled={loading}
                color="primary"
                variant="contained"
                onClick={() => onSubmit(methods.getValues())}
              >
                <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={loading} />
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
